import { Component, OnInit, computed, inject, signal, Input, Output, EventEmitter, input, ViewChildren, QueryList, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { MessageService } from 'primeng/api';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { CheckboxModule } from 'primeng/checkbox';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ButtonModule } from 'primeng/button';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { FormDropdownService } from '../../../../../core/services/form-dropdown.service';
import { GeographyService } from '../../../../../core/services/geography.service';
import { ProfessionalsService } from '../../../../../core/services/professionals.service';
import { ProfessionalsWizardService } from '../../../../services/professionals-wizard.service';
import { ProfessionalsDataService } from '../../../../services/professionals-data.service';
import { UserSessionService } from '../../../../../core/services/mock-auth.service';
import { hasMeaningfulText } from '../../../../../core/validators/text-value.utils';
import { ValidationErrorSection, validationSectionsFromMessages } from '../../../validation-error-summary/validation-error-summary.model';
import { convertEmptyStringsToNull } from '../../../../utils/form-data.utils';
import { catchError, of } from 'rxjs';
import { Bridge, Lift, SpecialVehicle, Fleet, Authorization, IsoCertification, EstimateSoftwareType } from '../../../../models';

interface Collaborator {
  id: number;
  surname: string;
  name: string;
  fiscalCode: string;
  claimsSystemAccess: boolean;
}

@Component({
  selector: 'app-bodyshop-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    MultiSelectModule,
    CheckboxModule,
    RadioButtonModule,
    ButtonModule,
    TextareaModule,
    TooltipModule,
  ],
  templateUrl: './bodyshop-form.component.html',
  styleUrls: ['./bodyshop-form.component.scss'],
})
export class BodyshopFormComponent implements OnInit {
  private router = inject(Router);
  private titleService = inject(Title);
  private messageService = inject(MessageService);
  private formDropdownService = inject(FormDropdownService);
  private geographyService = inject(GeographyService);
  private professionalsService = inject(ProfessionalsService);
  private professionalsWizardService = inject(ProfessionalsWizardService);
  private mockAuthService = inject(UserSessionService);
  private professionalsDataService = inject(ProfessionalsDataService);

  // Input for professional ID (when used in review/detail pages)
  professionalIdInput = input<string | null>(null);
  // Explicit readonly flag passed by parent
  readonly = input<boolean>(false);

  // Whether this is an NK_BODYSHOP (network bodyshop) onboarding — affects displayed label
  isNetworkBodyShop = input<boolean>(false);

  isReadOnly = computed(() => this.professionalsDataService.isReadOnly() || this.readonly());

  // Input for body shop type from parent component
  @Input() set tipologiaCarrozzeria(value: string | null) {
    if (value) {
      this.bodyShopType.set(value);
      console.log('[INFO] [Bodyshop Form] Tipologia Carrozzeria received from parent:', value);
    }
  }

  // Output events for parent component integration
  @Output() onSave = new EventEmitter<any>();
  @Output() onSaveDraft = new EventEmitter<any>();
  @Output() validationFailed = new EventEmitter<ValidationErrorSection[]>();

  readonly hasMeaningfulText = hasMeaningfulText;

  // Body shop type
  bodyShopType = signal<string>('STANDARD');
  isAxaDeutscheBank = computed(() => this.bodyShopType() === 'AXA_DEUTSCHE_BANK');

  // Sezione 1: INFORMAZIONI STRUTTURA
  isPresentOnline = signal<boolean | null>(null);
  typePresenceOnline = signal<string>('');
  webPresenceUrl = signal<string>('');
  otherOnlinePresence = signal<string>('');
  manageGoogleMyBusiness = signal<boolean | null>(null);
  morningOpeningHours = signal<string>('');
  afternoonOpeningHours = signal<string>('');
  saturdayOpeningHours = signal<string>('');
  coveredArea = signal<number | null>(null);
  uncoveredArea = signal<number | null>(null);
  storageArea = signal<number | null>(null);
  customerReceptionArea = signal<number | null>(null);
  hasAccessForDisabledPeople = signal<boolean | null>(null);
  hasCustomerParking = signal<boolean | null>(null);
  operatingTerritories = signal<string[]>([]);

  // Sezione 1 - ATTREZZATURE
  paintBoothsCount = signal<number | null>(null);
  paintBoothsInstallationYear = signal<number | null>(null);
  paintBrand = signal<string>('');
  dryingType = signal<string>('');
  dryingTypeOther = signal<string>('');
  isPresentTinometer = signal<boolean | null>(null);
  aluminumWork = signal<boolean | null>(null);
  specificEquipment = signal<string>('');
  liftsCount = signal<number | null>(null);
  bridges = signal<Bridge[]>([]);
  hasJig = signal<boolean | null>(null);
  jigType = signal<string>('');
  jigTypeOther = signal<string>('');
  hasDigitalMeasurement = signal<boolean | null>(null);
  digitalMeasurementType = signal<string>('');
  otherDigitalMeasurement = signal<string>('');
  dustExtractionFloors = signal<number | null>(null);
  dustExtractionArms = signal<number | null>(null);

  // Sezione 2: SERVIZI
  hasWashingService = signal<boolean | null>(null);
  washingServiceType = signal<string>('');
  hasMechanicsService = signal<boolean | null>(null);
  mechanicsServiceType = signal<string>('');
  hasElectronicDiagnostics = signal<boolean | null>(null);
  electronicDiagnosticsType = signal<string>('');
  hasElectricalService = signal<boolean | null>(null);
  electricalServiceType = signal<string>('');
  hasTireService = signal<boolean | null>(null);
  tireServiceType = signal<string>('');
  hasAirConditioningRecharge = signal<boolean | null>(null);
  airConditioningRechargeType = signal<string>('');
  hasMinisterialInspection = signal<boolean | null>(null);
  ministerialInspectionType = signal<string>('');
  hasAdasSystemsCalibration = signal<boolean | null>(null);
  adasSystemsCalibrationLocation = signal<string>('');
  sellsMotorVehicles = signal<boolean | null>(null);

  // Sezione 3: RIPARAZIONI
  hasSmartServices = signal<boolean | null>(null);
  hasHailDamageRepair = signal<boolean | null>(null);
  hasWindshieldRepair = signal<boolean | null>(null);
  hasFabricRepair = signal<boolean | null>(null);
  hasPlasticLeatherRepair = signal<boolean | null>(null);
  hasRimRepair = signal<boolean | null>(null);
  hasSpecificPointRepair = signal<boolean | null>(null);
  makeRepairsUsingColdRepairTechniques = signal<boolean | null>(null);
  hasSpotRepairs = signal<boolean | null>(null);
  hasSpecialVehicleRepairs = signal<boolean | null>(null);
  specialVehicles = signal<SpecialVehicle[]>([]);
  hasSubstituteCar = signal<boolean | null>(null);
  rentalCarsCount = signal<number | null>(null);
  ownedCarsCount = signal<number | null>(null);
  hasRoadsideAssistance20km = signal<boolean | null>(null);
  roadsideAssistanceType = signal<string>('');
  ownedTowTrucksCount = signal<number | null>(null);
  hasPickupDelivery20km = signal<boolean | null>(null);
  hasInteriorWashing = signal<boolean | null>(null);
  hasFinishingTouches = signal<boolean | null>(null);
  hasEngineLevelCheck = signal<boolean | null>(null);
  hasTirePressureCheck = signal<boolean | null>(null);
  hasAirConditioningCleaning = signal<boolean | null>(null);
  hasCabinSanitization = signal<boolean | null>(null);
  hasMechanicalCheckup = signal<boolean | null>(null);

  // Sezione 4: FLOTTE E CERTIFICAZIONI
  hasRentalVehicleRepairs = signal<boolean | null>(null);
  fleets = signal<Fleet[]>([]);
  isTeslaAuthorized = signal<boolean | null>(null);
  authorizations = signal<Authorization[]>([]);
  hasSafetyCertifications = signal<boolean | null>(null);
  hasSwornCertifications = signal<boolean | null>(null);
  hasGreenCertifications = signal<boolean | null>(null);
  greenCertifications = signal<string>('');
  hasIsoCertifications = signal<boolean | null>(null);
  isoCertifications = signal<IsoCertification[]>([]);
  hasHybridCourtesyCars = signal<boolean | null>(null);
  hasChargingColumns = signal<boolean | null>(null);
  hasEcologicalPaintCertifications = signal<boolean | null>(null);
  hasSolarPanels = signal<boolean | null>(null);
  hasEndothermicBoothsWithLowCo2Emission = signal<boolean | null>(null);
  usesRecycledOeReplacementParts = signal<boolean | null>(null);
  usesDigitalTools = signal<boolean | null>(null);
  otherCertifications = signal<string>('');

  // Sezione 5: CONVENZIONI
  hasAgreementWithAxaAssistance = signal<boolean | null>(null);
  hasAgreementWithOtherInsuranceCompanies = signal<boolean | null>(null);
  otherInsuranceConventions = signal<string>('');
  hasAgreementWithHailProviders = signal<boolean | null>(null);
  hailProvidersInAgreement = signal<string>('');
  hasAgreementWithCrystalProviders = signal<boolean | null>(null);
  crystalProvidersInAgreement = signal<string>('');
  isPartOfNetwork = signal<boolean | null>(null);
  nameOfNetwork = signal<string>('');
  isAffiliatedWithLongTermRentalFleetNetwork = signal<boolean | null>(null);
  nameOfLongTermRentalFleetNetwork = signal<string>('');
  isAffiliatedWithShortTermRentalFleetNetwork = signal<boolean | null>(null);
  nameOfShortTermRentalFleetNetwork = signal<string>('');

  // Sezione 6: RISORSE UMANE E DATI OPERATIVI
  employeesCount = signal<number | null>(null);
  tinsmithsCount = signal<number | null>(null);
  preparersCount = signal<number | null>(null);
  paintersCount = signal<number | null>(null);
  assemblyWorkersCount = signal<number | null>(null);
  clerksCount = signal<number | null>(null);
  hasTrainingCourses = signal<boolean | null>(null);
  hasSoftwarePreventivi = signal<boolean | null>(null);
  totalComputers = signal<number | null>(null);
  estimateSoftwareTypes = signal<EstimateSoftwareType[]>([]);

  // AXA_DEUTSCHE_BANK-only fields
  legalRepresentativeFirstName = signal<string>('');
  legalRepresentativeLastName = signal<string>('');
  legalRepresentativeFiscalCode = signal<string>('');
  hasWindshieldReplacement = signal<boolean | null>(null);
  hasMotorcycleRepair = signal<boolean | null>(null);
  hasRelativesInAxa = signal<string>('');
  hasEconomicRelationships = signal<string>('');

  // Capoluogo di provincia
  isProvinceCapital = signal<boolean | null>(null);

  // Computed: true if this professional is an operational location (parentProfessionalId is set)
  // Also true for NK_BODYSHOP, which are network body shops that have an operational address
  isOperationalLocation = computed(() =>
    this.professionalsWizardService.isOperationalLocationOnboarding() || this.isNetworkBodyShop()
  );

  // Operational location address fields (only filled when isOperationalLocation)
  opAddressProvince = signal<string>('');
  opAddressMunicipality = signal<string>('');
  opAddressRegion = signal<string>('');
  opAddressStreetType = signal<string>('');
  opAddressStreetName = signal<string>('');
  opAddressStreetNumber = signal<string>('');
  opAddressZipCode = signal<string>('');
  // Municipality options for operational address (filtered by province)
  opMunicipalityOptions = signal<{ label: string; value: string; isProvinceCapital?: boolean }[]>([]);

  // Dropdown options — BODY_SHOP_TYPE is static, all others loaded via FormDropdownService (form: BODY_SHOP_SP)
  bodyShopTypeOptions = signal<{ label: string; value: string }[]>([
    { label: 'STANDARD - Carrozzeria Standard', value: 'STANDARD' },
    { label: 'ARS - AXA Repair Services', value: 'ARS' },
    { label: 'RCI - Rete Carrozzerie Italiane', value: 'RCI' },
    { label: 'NOBIS - Carrozzeria NOBIS', value: 'NOBIS' },
    { label: 'AXA Deutsche Bank', value: 'AXA_DEUTSCHE_BANK' },
  ]);
  webPresenceTypeOptions = signal<{ label: string; value: string }[]>([]);
  dryingTypeOptions = signal<{ label: string; value: string }[]>([]);
  liftTypeOptions = signal<{ label: string; value: string }[]>([]);
  jigTypeOptions = signal<{ label: string; value: string }[]>([]);
  digitalMeasurementTypeOptions = signal<{ label: string; value: string }[]>([]);
  serviceTypeOptions = signal<{ label: string; value: string }[]>([]);
  roadsideAssistanceTypeOptions = signal<{ label: string; value: string }[]>([]);
  specialVehicleTypeOptions = signal<{ label: string; value: string }[]>([]);
  carManufacturerOptions = signal<{ label: string; value: string }[]>([]);
  isoCertificationOptions = signal<{ label: string; value: string }[]>([]);
  estimateSoftwareTypeOptions = signal<{ label: string; value: string }[]>([]);

  // Province options  loaded from BE
  provinceOptions = signal<{ label: string; value: string }[]>([]);

  // Street type options (shared with general-data, form: PLACE_NAME)
  streetTypeOptions = signal<{ label: string; value: string }[]>([]);

  // Counters for repeatable items
  private bridgeIdCounter = 1;
  private specialVehicleIdCounter = 1;
  private fleetIdCounter = 1;
  private authorizationIdCounter = 1;
  private isoCertIdCounter = 1;
  private estimateSoftwareIdCounter = 1;

  // State management
  formSubmitted = signal<boolean>(false);
  touchedFields = new Set<string>();
  isSaving = signal<boolean>(false);
  liveRegionMessage = signal<string>('');

  @ViewChildren('firstInput') firstInputs!: QueryList<ElementRef>;

  // Collaborators
  collaborators = signal<Collaborator[]>([]);
  private collaboratorIdCounter = 1;

  /** Validates a time range string in format "HH:MM-HH:MM" */
  isValidTimeRange(value: string | null | undefined): boolean {
    if (!value?.trim()) return false;
    const match = value.trim().match(/^([0-1]\d|2[0-3]):([0-5]\d)-([0-1]\d|2[0-3]):([0-5]\d)$/);
    return match !== null;
  }

  // Computed: Section validation
  section1Errors = computed(() => {
    const errors: string[] = [];
    if (this.isAxaDeutscheBank()) {
      if (this.coveredArea() == null) errors.push('Area coperta obbligatoria');
      if (this.uncoveredArea() == null) errors.push('Area scoperta obbligatoria');
      if (this.storageArea() == null) errors.push('Area rimessaggio obbligatoria');
      return errors;
    }
    const morning = this.morningOpeningHours()?.trim();
    const afternoon = this.afternoonOpeningHours()?.trim();
    const saturday = this.saturdayOpeningHours()?.trim();
    if (!morning) errors.push('Orario apertura mattina obbligatorio');
    else if (!this.isValidTimeRange(morning)) errors.push('Formato orario mattina non valido (es: 08:30-12:30)');
    if (!afternoon) errors.push('Orario apertura pomeriggio obbligatorio');
    else if (!this.isValidTimeRange(afternoon)) errors.push('Formato orario pomeriggio non valido (es: 14:00-18:00)');
    if (!saturday) errors.push('Orario apertura sabato obbligatorio');
    else if (!this.isValidTimeRange(saturday)) errors.push('Formato orario sabato non valido (es: 08:30-12:30)');
    if (this.coveredArea() == null) errors.push('Area coperta obbligatoria');
    if (this.uncoveredArea() == null) errors.push('Area scoperta obbligatoria');
    if (this.storageArea() == null) errors.push('Area rimessaggio obbligatoria');
    if (this.customerReceptionArea() == null) errors.push('Area accoglienza clienti obbligatoria');
    if (this.paintBoothsCount() == null) errors.push('Numero cabine forno obbligatorio');
    if (this.isPresentTinometer() === null) errors.push('Tintometro obbligatorio');
    if (this.aluminumWork() === null) errors.push('Interventi alluminio obbligatorio');
    if (!this.paintBoothsInstallationYear()) errors.push('Anno installazione forno obbligatorio');
    if (!this.paintBrand()?.trim()) errors.push('Vernice marca utilizzata obbligatoria');
    if (!this.dryingType()) errors.push('Tipo essicazione obbligatorio');
    if (this.dryingType() === 'OTHER' && !this.dryingTypeOther()?.trim()) errors.push('Specificare tipo essicazione');
    if (this.liftsCount() == null) errors.push('Numero ponti sollevatori obbligatorio');
    if (this.bridges().some(b => b.bridgeType === 'OTHER' && !b.bridgeTypeOther?.trim())) errors.push('Specificare il tipo per tutti i ponti marcati come "Altro"');
    if ((this.liftsCount() ?? 0) > 0 && this.bridges().length === 0) errors.push('Aggiungere almeno un tipo di ponte');
    if (this.liftsCount() != null && this.liftsCount() !== this.bridges().length) errors.push(`Il numero di ponti sollevatori (${this.liftsCount()}) non corrisponde ai tipi di ponte configurati (${this.bridges().length})`);
    if (this.hasJig() === null) errors.push('Dima obbligatoria');
    if (this.hasJig() === true && !this.jigType()) errors.push('Tipo dima obbligatoria');
    if (this.hasJig() === true && this.jigType() === 'OTHER' && !this.jigTypeOther()?.trim()) errors.push('Specificare tipo dima');
    if (this.hasDigitalMeasurement() === null) errors.push('Misurazione digitale scocche obbligatoria');
    if (this.hasDigitalMeasurement() === true && !this.digitalMeasurementType()) errors.push('Tipo misurazione obbligatorio');
    if (this.hasDigitalMeasurement() === true && this.digitalMeasurementType() === 'OTHER' && !this.otherDigitalMeasurement()?.trim()) errors.push('Specificare tipo di misurazione');
    if (this.dustExtractionFloors() == null) errors.push('Polveri: piani aspiranti obbligatorio');
    if (this.dustExtractionArms() == null) errors.push('Polveri: bracci aspiranti obbligatorio');
    if (this.isPresentOnline() === null) errors.push("Indicare se l'azienda  presente sul web");
    if (this.manageGoogleMyBusiness() === null) errors.push("Indicare se l'azienda gestisce Google My Business");
    if (this.isPresentOnline() && !this.typePresenceOnline()) errors.push("Specificare come l'azienda  presente sul web");
    if (this.isPresentOnline() && this.typePresenceOnline() === 'WEBSITE' && !this.webPresenceUrl()?.trim()) errors.push('Inserire URL sito web');
    if (this.isPresentOnline() && this.typePresenceOnline() === 'OTHER' && !this.otherOnlinePresence()?.trim()) errors.push('Specificare altro tipo di presenza web');
    return errors;
  });

  section2Errors = computed(() => {
    const errors: string[] = [];
    if (this.isAxaDeutscheBank()) {
      if (this.hasMechanicsService() === null) errors.push('Servizio Meccanica obbligatorio');
      if (this.hasMechanicsService() && !this.mechanicsServiceType()) errors.push('Tipo servizio Meccanica obbligatorio');
      if (this.hasElectronicDiagnostics() === null) errors.push('Diagnosi Elettronica obbligatoria');
      if (this.hasElectronicDiagnostics() && !this.electronicDiagnosticsType()) errors.push('Tipo servizio Diagnosi Elettronica obbligatorio');
      if (this.hasTireService() === null) errors.push('Servizio Gomme obbligatorio');
      if (this.hasTireService() && !this.tireServiceType()) errors.push('Tipo servizio Gomme obbligatorio');
      if (this.hasAirConditioningRecharge() === null) errors.push('Ricarica Condizionatori obbligatoria');
      if (this.hasAirConditioningRecharge() && !this.airConditioningRechargeType()) errors.push('Tipo servizio Ricarica Condizionatori obbligatorio');
      if (this.hasMinisterialInspection() === null) errors.push('Revisione Ministeriale obbligatoria');
      if (this.hasMinisterialInspection() && !this.ministerialInspectionType()) errors.push('Tipo servizio Revisione Ministeriale obbligatorio');
      return errors;
    }
    if (this.hasWashingService() === null) errors.push('Servizio Lavaggio obbligatorio');
    if (this.hasWashingService() && !this.washingServiceType()) errors.push('Tipo servizio Lavaggio obbligatorio');
    if (this.hasMechanicsService() === null) errors.push('Servizio Meccanica obbligatorio');
    if (this.hasMechanicsService() && !this.mechanicsServiceType()) errors.push('Tipo servizio Meccanica obbligatorio');
    if (this.hasElectronicDiagnostics() === null) errors.push('Diagnosi Elettronica obbligatoria');
    if (this.hasElectronicDiagnostics() && !this.electronicDiagnosticsType()) errors.push('Tipo servizio Diagnosi Elettronica obbligatorio');
    if (this.hasElectricalService() === null) errors.push('Servizio Elettrauto obbligatorio');
    if (this.hasElectricalService() && !this.electricalServiceType()) errors.push('Tipo servizio Elettrauto obbligatorio');
    if (this.hasTireService() === null) errors.push('Servizio Gomme obbligatorio');
    if (this.hasTireService() && !this.tireServiceType()) errors.push('Tipo servizio Gomme obbligatorio');
    if (this.hasAirConditioningRecharge() === null) errors.push('Ricarica Condizionatori obbligatoria');
    if (this.hasAirConditioningRecharge() && !this.airConditioningRechargeType()) errors.push('Tipo servizio Ricarica Condizionatori obbligatorio');
    if (this.hasMinisterialInspection() === null) errors.push('Revisione Ministeriale obbligatoria');
    if (this.hasMinisterialInspection() && !this.ministerialInspectionType()) errors.push('Tipo servizio Revisione Ministeriale obbligatorio');
    if (this.hasAdasSystemsCalibration() === null) errors.push('Taratura sistemi ADAS obbligatoria');
    if (this.hasAdasSystemsCalibration() && !this.adasSystemsCalibrationLocation()) errors.push('Tipo servizio Taratura sistemi ADAS obbligatorio');
    if (this.sellsMotorVehicles() === null) errors.push('Commercio Autoveicoli obbligatorio');
    if (this.specialVehicles().some(v => v.vehicleType === 'OTHER' && !v.vehicleTypeOther?.trim())) errors.push('Specificare il tipo per tutti i veicoli speciali marcati come "Altro"');
    if (this.authorizations().some(a => a.carManufacturer === 'OTHER' && !a.carManufacturerOther?.trim())) errors.push('Specificare la casa automobilistica per tutte le autorizzazioni marcate come "Altro"');
    return errors;
  });

  section3Errors = computed(() => {
    const errors: string[] = [];
    if (this.isAxaDeutscheBank()) {
      if (this.hasWindshieldRepair() === null) errors.push('Cristalli Riparazione obbligatorio');
      if (this.hasWindshieldReplacement() === null) errors.push('Cristalli Sostituzione obbligatorio');
      if (this.hasMotorcycleRepair() === null) errors.push('Motoveicoli / Ciclomotori obbligatorio');
      if (this.hasSmartServices() === null) errors.push('Interventi smart obbligatorio');
      if (this.hasSpotRepairs() === null) errors.push('Riparazioni Spot obbligatorio');
      if (this.hasSpecialVehicleRepairs() === null) errors.push('Riparazioni Veicoli Particolari obbligatorio');
      if (this.hasSpecialVehicleRepairs() === true && this.specialVehicles().length === 0) errors.push('Aggiungere almeno una tipologia di veicolo');
      if (this.hasSubstituteCar() === null) errors.push('Auto Sostitutiva obbligatorio');
      if (this.hasRoadsideAssistance20km() === null) errors.push('Soccorso Stradale entro 20km obbligatorio');
      if (this.hasPickupDelivery20km() === null) errors.push('Pick up & Delivery - servizio presa e riconsegna veicolo a domicilio entro 20km obbligatorio');
      return errors;
    }
    // 48: Interventi smart
    if (this.hasSmartServices() === null) errors.push("Interventi smart obbligatorio");
    // 49: SMART: Riparazione Danni Grandine
    if (this.hasHailDamageRepair() === null) errors.push("SMART: Riparazione Danni Grandine obbligatorio");
    // 50: SMART: Riparazione Parabrezza
    if (this.hasWindshieldRepair() === null) errors.push("SMART: Riparazione Parabrezza obbligatorio");
    // 51: SMART: Riparazione Tessuti
    if (this.hasFabricRepair() === null) errors.push("SMART: Riparazione Tessuti obbligatorio");
    // 52: SMART: Riparazione Plastica - Pelle - Vinile
    if (this.hasPlasticLeatherRepair() === null) errors.push("SMART: Riparazione Plastica - Pelle - Vinile obbligatorio");
    // 53: SMART: Riparazione Cerchioni
    if (this.hasRimRepair() === null) errors.push("SMART: Riparazione Cerchioni obbligatorio");
    // 54: SMART: Riparazione Punti Specifici
    if (this.hasSpecificPointRepair() === null) errors.push("SMART: Riparazione Punti Specifici obbligatorio");
    // 55: Riparazioni di carrozzeria con le tecniche della riparazione a freddo
    if (this.makeRepairsUsingColdRepairTechniques() === null) errors.push("Riparazioni di carrozzeria con le tecniche della riparazione a freddo obbligatorio");
    // 56: Riparazioni Spot
    if (this.hasSpotRepairs() === null) errors.push("Riparazioni Spot obbligatorio");
    // 57: Riparazioni Veicoli Particolari
    if (this.hasSpecialVehicleRepairs() === null) errors.push("Riparazioni Veicoli Particolari obbligatorio");
    // 58-59: Tipologie Veicoli Particolari
    if (this.hasSpecialVehicleRepairs() && this.hasSpecialVehicleRepairs() === true && this.specialVehicles().length === 0) errors.push("Aggiungere almeno una tipologia di veicolo");
    // 61: Auto Sostitutiva
    if (this.hasSubstituteCar() === null) errors.push("Auto Sostitutiva obbligatorio");
    // 62: Auto a Noleggio n
    if (this.rentalCarsCount() == null) errors.push("Auto a Noleggio n obbligatorio");
    // 63: Auto di Propriet n
    if (this.ownedCarsCount() == null) errors.push("Auto di Propriet n obbligatorio");
    // 64: Soccorso Stradale entro 20km
    if (this.hasRoadsideAssistance20km() === null) errors.push("Soccorso Stradale entro 20km obbligatorio");
    // 65: Soccorso Stradale (Mezzi Propri/Esterno)
    if (this.hasRoadsideAssistance20km() && !this.roadsideAssistanceType()) errors.push("Soccorso Stradale (Mezzi Propri/Esterno) obbligatorio");
    // 66: Carroattrezzi di Propriet n
    if (this.ownedTowTrucksCount() == null) errors.push("Carroattrezzi di Propriet n obbligatorio");
    // 67: Pick up & Delivery - servizio presa e riconsegna veicolo a domicilio entro 20km
    if (this.hasPickupDelivery20km() === null) errors.push("Pick up & Delivery - servizio presa e riconsegna veicolo a domicilio entro 20km obbligatorio");
    // 68: Lavaggio esterno e Pulizia/Aspirazione
    if (this.hasInteriorWashing() === null) errors.push("Lavaggio esterno e Pulizia/Aspirazione obbligatorio");
    // 69: Finitura con Piccoli Ritocchi
    if (this.hasFinishingTouches() === null) errors.push("Finitura con Piccoli Ritocchi obbligatorio");
    // 70: Controllo livello olio motore
    if (this.hasEngineLevelCheck() === null) errors.push("Controllo livello olio motore obbligatorio");
    // 71: Controllo pressione pneumatici
    if (this.hasTirePressureCheck() === null) errors.push("Controllo pressione pneumatici obbligatorio");
    // 72: Pulizia impianto aria condizionata
    if (this.hasAirConditioningCleaning() === null) errors.push("Pulizia impianto aria condizionata obbligatorio");
    // 73: Sanificazione abitacolo
    if (this.hasCabinSanitization() === null) errors.push("Sanificazione abitacolo obbligatorio");
    // 74: Check up meccanico
    if (this.hasMechanicalCheckup() === null) errors.push("Check up meccanico obbligatorio");
    return errors;
  });

  section4Errors = computed(() => {
    const errors: string[] = [];
    if (this.isAxaDeutscheBank()) {
      if (this.authorizations().some(a => a.carManufacturer === 'OTHER' && !a.carManufacturerOther?.trim())) {
        errors.push('Specificare la casa automobilistica per tutte le autorizzazioni marcate come "Altro"');
      }
      return errors;
    }
    if (this.hasRentalVehicleRepairs() === null) errors.push('Riparazione Autovetture Noleggio obbligatoria');
    if (this.isTeslaAuthorized() === null) errors.push('Carrozzeria Autorizzata Tesla obbligatoria');
    if (this.hasSafetyCertifications() === null) errors.push('Certificazioni Legge 626 obbligatoria');
    if (this.hasSwornCertifications() === null) errors.push('Certificazioni Giurate obbligatoria');
    if (this.hasGreenCertifications() === null) errors.push('Certificazioni Green obbligatoria');
    if (this.hasGreenCertifications() && !this.greenCertifications()?.trim()) errors.push('Quali certificazioni Green obbligatorio');
    if (this.hasIsoCertifications() === null) errors.push('Certificazioni ISO obbligatoria');
    if (this.hasIsoCertifications() === true && this.isoCertifications().length === 0) errors.push('Aggiungi almeno una certificazione ISO');
    if (this.isoCertifications().some(c => !c.isoCertification)) errors.push('Selezionare il tipo per tutte le certificazioni ISO');
    if (this.isoCertifications().some(c => c.isoCertification === 'OTHER' && !c.otherIsoCertification?.trim())) errors.push('Specificare il tipo per tutte le certificazioni ISO marcate come "Altro"');
    if (this.hasHybridCourtesyCars() === null) errors.push('Vetture di cortesia ibride/elettriche obbligatorio');
    if (this.hasChargingColumns() === null) errors.push('Colonnine di ricarica obbligatorio');
    if (this.hasEcologicalPaintCertifications() === null) errors.push('Certificazioni verniciatura ecologica obbligatoria');
    if (this.hasSolarPanels() === null) errors.push('Pannelli solari obbligatorio');
    if (this.hasEndothermicBoothsWithLowCo2Emission() === null) errors.push('Forni endotermici obbligatorio');
    if (this.usesRecycledOeReplacementParts() === null) errors.push('Utilizzo parti OE riciclate obbligatorio');
    if (this.usesDigitalTools() === null) errors.push('Utilizzo strumenti digitali obbligatorio');
    return errors;
  });
  section5Errors = computed(() => {
    const errors: string[] = [];
    if (this.isAxaDeutscheBank()) {
      return errors;
    }
    if (this.hasAgreementWithAxaAssistance() === null) errors.push('Indicare se esiste una convenzione con AXA Assistance');
    if (this.hasAgreementWithOtherInsuranceCompanies() === null) errors.push('Indicare se convenzionato con altre Compagnie di Assicurazioni');
    if (this.hasAgreementWithOtherInsuranceCompanies() && !this.otherInsuranceConventions().trim()) errors.push('Specificare le compagnie assicurative convenzionate');
    if (this.hasAgreementWithHailProviders() === null) errors.push('Indicare se esiste una convenzione con Provider Grandine');
    if (this.hasAgreementWithHailProviders() && !this.hailProvidersInAgreement().trim()) errors.push('Specificare il provider grandine');
    if (this.hasAgreementWithCrystalProviders() === null) errors.push('Indicare se esiste una convenzione con Provider Cristalli');
    if (this.hasAgreementWithCrystalProviders() && !this.crystalProvidersInAgreement().trim()) errors.push('Specificare il provider cristalli');
    if (this.isPartOfNetwork() === null) errors.push('Indicare se appartiene a un network di carrozzerie');
    if (this.isPartOfNetwork() && !this.nameOfNetwork().trim()) errors.push('Specificare il network di carrozzerie');
    if (this.isAffiliatedWithLongTermRentalFleetNetwork() === null) errors.push('Indicare se affiliato a Network di noleggio a lungo termine');
    if (this.isAffiliatedWithLongTermRentalFleetNetwork() && !this.nameOfLongTermRentalFleetNetwork().trim()) errors.push('Specificare il network di noleggio a lungo termine');
    if (this.isAffiliatedWithShortTermRentalFleetNetwork() === null) errors.push('Indicare se affiliato a Network di noleggio a breve termine');
    if (this.isAffiliatedWithShortTermRentalFleetNetwork() && !this.nameOfShortTermRentalFleetNetwork().trim()) errors.push('Specificare il network di noleggio a breve termine');
    return errors;
  });

  section6Errors = computed(() => {
    const errors: string[] = [];
    if (this.isAxaDeutscheBank()) {
      if (this.employeesCount() == null) errors.push('N dipendenti obbligatorio');
      return errors;
    }
    if (this.employeesCount() == null) errors.push('N dipendenti obbligatorio');
    if (this.tinsmithsCount() == null) errors.push('N lattonieri obbligatorio');
    if (this.preparersCount() == null) errors.push('N preparatori obbligatorio');
    if (this.paintersCount() == null) errors.push('N verniciatori obbligatorio');
    if (this.assemblyWorkersCount() == null) errors.push('N addetti stacco/riattacco obbligatorio');
    if (this.clerksCount() == null) errors.push('N impiegati obbligatorio');
    if (this.hasTrainingCourses() === null) errors.push('Indicare se sono presenti corsi formativi');
    if (this.hasSoftwarePreventivi() === null) errors.push('Software Preventivi obbligatorio');
    return errors;
  });

  opAddressErrors = computed(() => {
    const errors: string[] = [];
    if (this.isOperationalLocation()) {
      if (!this.opAddressRegion()?.trim()) errors.push('Regione sede operativa obbligatoria');
      if (!this.opAddressProvince()) errors.push('Provincia sede operativa obbligatoria');
      if (!this.opAddressMunicipality()) errors.push('Comune sede operativa obbligatorio');
      if (!this.opAddressZipCode()?.trim()) errors.push('CAP sede operativa obbligatorio');
      if (!this.opAddressStreetType()?.trim()) errors.push('Tipo via sede operativa obbligatorio');
      if (!this.opAddressStreetName()?.trim()) errors.push('Indirizzo sede operativa obbligatorio');
      if (!this.opAddressStreetNumber()) errors.push('Civico sede operativa obbligatorio');
    }
    return errors;
  });

  isFormValid = computed(() =>
    this.opAddressErrors().length === 0 &&
    this.section1Errors().length === 0 &&
    this.section2Errors().length === 0 &&
    this.section3Errors().length === 0 &&
    this.section4Errors().length === 0 &&
    this.section5Errors().length === 0 &&
    this.section6Errors().length === 0
  );

  ngOnInit(): void {
    this.titleService.setTitle('Portale Fiduciari - Dati Specifici Carrozzeria');
    console.log('[INFO] Initializing Bodyshop Form (v2)');
    this.loadProvinceOptions();
    this.loadDropdownOptions();
    this.loadData();
  }

  private loadProvinceOptions(): void {
    this.geographyService
      .getProvinces()
      .pipe(catchError(() => of([])))
      .subscribe((provinces) => {
        this.provinceOptions.set(
          (provinces || []).map((item: any) => ({ label: item.name, value: item.code }))
        );
      });
  }

  /**
   * Called when the province for the operational location address changes.
   * Loads municipalities and resets municipality + isProvinceCapital.
   */
  onOpAddressProvinceChange(provinceCode: string): void {
    this.opAddressMunicipality.set('');
    this.isProvinceCapital.set(null);
    if (!provinceCode) {
      this.opMunicipalityOptions.set([]);
      return;
    }
    this.geographyService
      .getMunicipalities(provinceCode)
      .pipe(catchError(() => of([])))
      .subscribe((municipalities) => {
        this.opMunicipalityOptions.set(
          (municipalities || []).map((m: any) => ({
            label: m.name,
            value: m.code,
            isProvinceCapital: m.isProvinceCapital ?? false,
          }))
        );
      });
  }

  /**
   * Used during data load: loads municipality options for the saved province
   * and then restores the saved municipality value (no reset).
   */
  private preloadOpAddressMunicipalities(provinceCode: string, savedMunicipality: string): void {
    this.geographyService
      .getMunicipalities(provinceCode)
      .pipe(catchError(() => of([])))
      .subscribe((municipalities) => {
        this.opMunicipalityOptions.set(
          (municipalities || []).map((m: any) => ({
            label: m.name,
            value: m.code,
            isProvinceCapital: m.isProvinceCapital ?? false,
          }))
        );
        // Restore saved value after options are loaded
        if (savedMunicipality) {
          this.opAddressMunicipality.set(savedMunicipality);
        }
      });
  }

  /**
   * Called when the municipality for the operational location address changes.
   * Auto-sets isProvinceCapital based on the selected municipality.
   */
  onOpAddressMunicipalityChange(municipalityName: string): void {
    const match = this.opMunicipalityOptions().find(m => m.value === municipalityName);
    if (match) {
      this.isProvinceCapital.set(match.isProvinceCapital ?? false);
    }
  }

  private loadDropdownOptions(): void {
    const FORM_ID = 'BODY_SHOP_SP';
    const load = (fieldId: string, setter: (opts: { label: string; value: string }[]) => void) => {
      this.formDropdownService.getFormDropdownValues(FORM_ID, fieldId)
        .pipe(catchError(() => of({ status: false, message: '', data: [] })))
        .subscribe(r => setter(((r as any).data || []).map((i: any) => ({ label: i.valueDescription, value: i.valueKey }))));
    };
    load('WEB_PRESENCE_TYPE', opts => this.webPresenceTypeOptions.set(opts));
    load('DRYING_TYPE', opts => this.dryingTypeOptions.set(opts));
    load('LIFT_TYPE', opts => this.liftTypeOptions.set(opts));
    load('JIG_TYPE', opts => this.jigTypeOptions.set(opts));
    load('DIGITAL_MEASUREMENT_TYPE', opts => this.digitalMeasurementTypeOptions.set(opts));
    load('SERVICE_TYPE', opts => this.serviceTypeOptions.set(opts));
    load('ROADSIDE_ASSISTANCE_TYPE', opts => this.roadsideAssistanceTypeOptions.set(opts));
    load('SPECIAL_VEHICLE_TYPE', opts => this.specialVehicleTypeOptions.set(opts));
    load('CAR_MANUFACTURER', opts => this.carManufacturerOptions.set(opts));
    load('ISO_CERTIFICATION', opts => this.isoCertificationOptions.set(opts));
    load('ESTIMATE_SOFTWARE_TYPE', opts => this.estimateSoftwareTypeOptions.set(opts));

    // Street type dropdown (same source as general-data)
    this.formDropdownService.getFormDropdownValues('PLACE_NAME', 'PLACE_NAME')
      .pipe(catchError(() => of({ status: false, message: '', data: [] })))
      .subscribe(r => this.streetTypeOptions.set(((r as any).data || []).map((i: any) => ({ label: i.valueDescription, value: i.valueKey }))));
  }

  loadData(): void {
    const professionalId = this.professionalIdInput() || this.mockAuthService.getDemoProfessionalId();

    if (!professionalId) {
      console.warn('[WARN] No professional ID found, starting with empty form');
      return;
    }

    console.log(`[INFO] Loading bodyshop data for professional ID: ${professionalId} (isNetworkBodyShop: ${this.isNetworkBodyShop()})`);

    this.professionalsService
      .getBodyshopData(professionalId)
      .pipe(catchError(() => of(null)))
      .subscribe({
        next: (data: any) => {
          if (data) {
            console.log('[OK] Bodyshop data loaded:', data);
            const bodyShop = data?.data ?? data;
            this.populateFormData(bodyShop);
          } else {
            console.log('[INFO] No existing bodyshop data found');
            // Mark step as NOT completed when no data exists
            this.professionalsWizardService.markStepCompleted(1, false);
            this.professionalsWizardService.saveState();
          }
        },
        error: (err: any) => {
          console.error('[ERR] Error loading bodyshop data:', err);
          // Mark step as NOT completed on error
          this.professionalsWizardService.markStepCompleted(1, false);
          this.professionalsWizardService.saveState();
        },
      });
  }

  populateFormData(data: any): void {
    if (!data.specificData) return;
    const sd = data.specificData;

    // Body shop type — sanitize: 'NK_BODYSHOP' is not a valid form subtype, keep current value
    const validBodyShopTypes = ['STANDARD', 'ARS', 'RCI', 'NOBIS', 'AXA_DEUTSCHE_BANK'];
    if (sd.bodyShopType && validBodyShopTypes.includes(sd.bodyShopType)) {
      this.bodyShopType.set(sd.bodyShopType);
    }

    // Sezione 1 - Informazioni Struttura
    if (sd.isPresentOnline !== undefined) this.isPresentOnline.set(sd.isPresentOnline);
    if (sd.typePresenceOnline) this.typePresenceOnline.set(sd.typePresenceOnline);
    if (sd.webPresenceUrl) this.webPresenceUrl.set(sd.webPresenceUrl);
    if (sd.otherOnlinePresence) this.otherOnlinePresence.set(sd.otherOnlinePresence);
    if (sd.manageGoogleMyBusiness !== undefined) this.manageGoogleMyBusiness.set(sd.manageGoogleMyBusiness);
    if (sd.morningOpeningHours) this.morningOpeningHours.set(sd.morningOpeningHours);
    if (sd.afternoonOpeningHours) this.afternoonOpeningHours.set(sd.afternoonOpeningHours);
    if (sd.saturdayOpeningHours) this.saturdayOpeningHours.set(sd.saturdayOpeningHours);
    if (sd.coveredArea != null) this.coveredArea.set(sd.coveredArea);
    if (sd.uncoveredArea != null) this.uncoveredArea.set(sd.uncoveredArea);
    if (sd.storageArea != null) this.storageArea.set(sd.storageArea);
    if (sd.customerReceptionArea != null) this.customerReceptionArea.set(sd.customerReceptionArea);
    if (sd.hasAccessForDisabledPeople !== undefined) this.hasAccessForDisabledPeople.set(sd.hasAccessForDisabledPeople);
    if (sd.hasCustomerParking !== undefined) this.hasCustomerParking.set(sd.hasCustomerParking);
    if (sd.operatingTerritories?.length) this.operatingTerritories.set(sd.operatingTerritories);

    // Attrezzature
    if (sd.paintBoothsCount != null) this.paintBoothsCount.set(sd.paintBoothsCount);
    if (sd.paintBoothsInstallationYear != null) this.paintBoothsInstallationYear.set(sd.paintBoothsInstallationYear);
    if (sd.paintBrand) this.paintBrand.set(sd.paintBrand);
    if (sd.dryingType) this.dryingType.set(sd.dryingType);
    if (sd.dryingTypeOther) this.dryingTypeOther.set(sd.dryingTypeOther);
    if (sd.isPresentTinometer !== undefined) this.isPresentTinometer.set(sd.isPresentTinometer);
    if (sd.aluminumWork !== undefined) this.aluminumWork.set(sd.aluminumWork);
    if (sd.specificEquipment) this.specificEquipment.set(sd.specificEquipment);
    if (sd.liftsCount != null) this.liftsCount.set(sd.liftsCount);
    if (Array.isArray(sd.bridges)) {
      this.bridges.set(sd.bridges.map((b: any) => ({
        id: this.bridgeIdCounter++,
        bridgeType: b.bridgeType || '',
        bridgeTypeOther: b.bridgeTypeOther || '',
      })));
    }
    if (sd.hasJig !== undefined) this.hasJig.set(sd.hasJig);
    if (sd.jigType) this.jigType.set(sd.jigType);
    if (sd.jigTypeOther) this.jigTypeOther.set(sd.jigTypeOther);
    if (sd.hasDigitalMeasurement !== undefined) this.hasDigitalMeasurement.set(sd.hasDigitalMeasurement);
    if (sd.digitalMeasurementType) this.digitalMeasurementType.set(sd.digitalMeasurementType);
    if (sd.otherDigitalMeasurement) this.otherDigitalMeasurement.set(sd.otherDigitalMeasurement);
    if (sd.dustExtractionFloors != null) this.dustExtractionFloors.set(sd.dustExtractionFloors);
    if (sd.dustExtractionArms != null) this.dustExtractionArms.set(sd.dustExtractionArms);

    // Sezione 2 - Servizi
    if (sd.hasWashingService !== undefined) this.hasWashingService.set(sd.hasWashingService);
    if (sd.washingServiceType) this.washingServiceType.set(sd.washingServiceType);
    if (sd.hasMechanicsService !== undefined) this.hasMechanicsService.set(sd.hasMechanicsService);
    if (sd.mechanicsServiceType) this.mechanicsServiceType.set(sd.mechanicsServiceType);
    if (sd.hasElectronicDiagnostics !== undefined) this.hasElectronicDiagnostics.set(sd.hasElectronicDiagnostics);
    if (sd.electronicDiagnosticsType) this.electronicDiagnosticsType.set(sd.electronicDiagnosticsType);
    if (sd.hasElectricalService !== undefined) this.hasElectricalService.set(sd.hasElectricalService);
    if (sd.electricalServiceType) this.electricalServiceType.set(sd.electricalServiceType);
    if (sd.hasTireService !== undefined) this.hasTireService.set(sd.hasTireService);
    if (sd.tireServiceType) this.tireServiceType.set(sd.tireServiceType);
    if (sd.hasAirConditioningRecharge !== undefined) this.hasAirConditioningRecharge.set(sd.hasAirConditioningRecharge);
    if (sd.airConditioningRechargeType) this.airConditioningRechargeType.set(sd.airConditioningRechargeType);
    if (sd.hasMinisterialInspection !== undefined) this.hasMinisterialInspection.set(sd.hasMinisterialInspection);
    if (sd.ministerialInspectionType) this.ministerialInspectionType.set(sd.ministerialInspectionType);
    if (sd.hasAdasSystemsCalibration !== undefined) this.hasAdasSystemsCalibration.set(sd.hasAdasSystemsCalibration);
    if (sd.adasSystemsCalibrationLocation) this.adasSystemsCalibrationLocation.set(sd.adasSystemsCalibrationLocation);
    if (sd.sellsMotorVehicles !== undefined) this.sellsMotorVehicles.set(sd.sellsMotorVehicles);

    // Sezione 3 - Riparazioni
    if (sd.hasSmartServices !== undefined) this.hasSmartServices.set(sd.hasSmartServices);
    if (sd.hasHailDamageRepair !== undefined) this.hasHailDamageRepair.set(sd.hasHailDamageRepair);
    if (sd.hasWindshieldRepair !== undefined) this.hasWindshieldRepair.set(sd.hasWindshieldRepair);
    if (sd.hasFabricRepair !== undefined) this.hasFabricRepair.set(sd.hasFabricRepair);
    if (sd.hasPlasticLeatherRepair !== undefined) this.hasPlasticLeatherRepair.set(sd.hasPlasticLeatherRepair);
    if (sd.hasRimRepair !== undefined) this.hasRimRepair.set(sd.hasRimRepair);
    if (sd.hasSpecificPointRepair !== undefined) this.hasSpecificPointRepair.set(sd.hasSpecificPointRepair);
    if (sd.makeRepairsUsingColdRepairTechniques !== undefined) this.makeRepairsUsingColdRepairTechniques.set(sd.makeRepairsUsingColdRepairTechniques);
    if (sd.hasSpotRepairs !== undefined) this.hasSpotRepairs.set(sd.hasSpotRepairs);
    if (sd.hasSpecialVehicleRepairs !== undefined) this.hasSpecialVehicleRepairs.set(sd.hasSpecialVehicleRepairs);
    if (Array.isArray(sd.specialVehicles)) {
      this.specialVehicles.set(sd.specialVehicles.map((sv: any) => ({
        id: this.specialVehicleIdCounter++,
        vehicleType: sv.vehicleType || '',
        vehicleTypeOther: sv.vehicleTypeOther || '',
      })));
    }
    if (sd.hasSubstituteCar !== undefined) this.hasSubstituteCar.set(sd.hasSubstituteCar);
    if (sd.rentalCarsCount != null) this.rentalCarsCount.set(sd.rentalCarsCount);
    if (sd.ownedCarsCount != null) this.ownedCarsCount.set(sd.ownedCarsCount);
    if (sd.hasRoadsideAssistance20km !== undefined) this.hasRoadsideAssistance20km.set(sd.hasRoadsideAssistance20km);
    if (sd.roadsideAssistanceType) this.roadsideAssistanceType.set(sd.roadsideAssistanceType);
    if (sd.ownedTowTrucksCount != null) this.ownedTowTrucksCount.set(sd.ownedTowTrucksCount);
    if (sd.hasPickupDelivery20km !== undefined) this.hasPickupDelivery20km.set(sd.hasPickupDelivery20km);
    if (sd.hasInteriorWashing !== undefined) this.hasInteriorWashing.set(sd.hasInteriorWashing);
    if (sd.hasFinishingTouches !== undefined) this.hasFinishingTouches.set(sd.hasFinishingTouches);
    if (sd.hasEngineLevelCheck !== undefined) this.hasEngineLevelCheck.set(sd.hasEngineLevelCheck);
    if (sd.hasTirePressureCheck !== undefined) this.hasTirePressureCheck.set(sd.hasTirePressureCheck);
    if (sd.hasAirConditioningCleaning !== undefined) this.hasAirConditioningCleaning.set(sd.hasAirConditioningCleaning);
    if (sd.hasCabinSanitization !== undefined) this.hasCabinSanitization.set(sd.hasCabinSanitization);
    if (sd.hasMechanicalCheckup !== undefined) this.hasMechanicalCheckup.set(sd.hasMechanicalCheckup);

    // Sezione 4 - Flotte e Certificazioni
    if (sd.hasRentalVehicleRepairs !== undefined) this.hasRentalVehicleRepairs.set(sd.hasRentalVehicleRepairs);
    if (Array.isArray(sd.fleets)) {
      this.fleets.set(sd.fleets.map((f: any) => ({ id: this.fleetIdCounter++, fleetName: f.fleetName || '' })));
    }
    if (sd.isTeslaAuthorized !== undefined) this.isTeslaAuthorized.set(sd.isTeslaAuthorized);
    if (Array.isArray(sd.authorizations)) {
      this.authorizations.set(sd.authorizations.map((a: any) => ({
        id: this.authorizationIdCounter++,
        carManufacturer: a.carManufacturer || '',
        carManufacturerOther: a.carManufacturerOther || '',
      })));
    }
    if (sd.hasSafetyCertifications !== undefined) this.hasSafetyCertifications.set(sd.hasSafetyCertifications);
    if (sd.hasSwornCertifications !== undefined) this.hasSwornCertifications.set(sd.hasSwornCertifications);
    if (sd.hasGreenCertifications !== undefined) this.hasGreenCertifications.set(sd.hasGreenCertifications);
    if (sd.greenCertifications) this.greenCertifications.set(sd.greenCertifications);
    if (sd.hasIsoCertifications !== undefined) this.hasIsoCertifications.set(sd.hasIsoCertifications);
    if (Array.isArray(sd.isoCertifications)) {
      this.isoCertifications.set((sd.isoCertifications as any[]).map(c => ({
        id: this.isoCertIdCounter++,
        isoCertification: c.isoCertification || '',
        otherIsoCertification: c.otherIsoCertification || null,
      })));
    }
    if (sd.hasHybridCourtesyCars !== undefined) this.hasHybridCourtesyCars.set(sd.hasHybridCourtesyCars);
    if (sd.hasChargingColumns !== undefined) this.hasChargingColumns.set(sd.hasChargingColumns);
    if (sd.hasEcologicalPaintCertifications !== undefined) this.hasEcologicalPaintCertifications.set(sd.hasEcologicalPaintCertifications);
    if (sd.hasSolarPanels !== undefined) this.hasSolarPanels.set(sd.hasSolarPanels);
    if (sd.hasEndothermicBoothsWithLowCo2Emission !== undefined) this.hasEndothermicBoothsWithLowCo2Emission.set(sd.hasEndothermicBoothsWithLowCo2Emission);
    if (sd.usesRecycledOeReplacementParts !== undefined) this.usesRecycledOeReplacementParts.set(sd.usesRecycledOeReplacementParts);
    if (sd.usesDigitalTools !== undefined) this.usesDigitalTools.set(sd.usesDigitalTools);
    if (sd.otherCertifications) this.otherCertifications.set(sd.otherCertifications);

    // Sezione 5 - Convenzioni
    if (sd.hasAgreementWithAxaAssistance !== undefined) this.hasAgreementWithAxaAssistance.set(sd.hasAgreementWithAxaAssistance);
    if (sd.hasAgreementWithOtherInsuranceCompanies !== undefined) this.hasAgreementWithOtherInsuranceCompanies.set(sd.hasAgreementWithOtherInsuranceCompanies);
    if (sd.hasAgreementWithHailProviders !== undefined) this.hasAgreementWithHailProviders.set(sd.hasAgreementWithHailProviders);
    if (sd.hailProvidersInAgreement) this.hailProvidersInAgreement.set(sd.hailProvidersInAgreement);
    if (sd.hasAgreementWithCrystalProviders !== undefined) this.hasAgreementWithCrystalProviders.set(sd.hasAgreementWithCrystalProviders);
    if (sd.crystalProvidersInAgreement) this.crystalProvidersInAgreement.set(sd.crystalProvidersInAgreement);
    if (sd.isPartOfNetwork !== undefined) this.isPartOfNetwork.set(sd.isPartOfNetwork);
    if (sd.nameOfNetwork) this.nameOfNetwork.set(sd.nameOfNetwork);
    if (sd.isAffiliatedWithLongTermRentalFleetNetwork !== undefined) this.isAffiliatedWithLongTermRentalFleetNetwork.set(sd.isAffiliatedWithLongTermRentalFleetNetwork);
    if (sd.nameOfLongTermRentalFleetNetwork) this.nameOfLongTermRentalFleetNetwork.set(sd.nameOfLongTermRentalFleetNetwork);
    if (sd.isAffiliatedWithShortTermRentalFleetNetwork !== undefined) this.isAffiliatedWithShortTermRentalFleetNetwork.set(sd.isAffiliatedWithShortTermRentalFleetNetwork);
    if (sd.nameOfShortTermRentalFleetNetwork) this.nameOfShortTermRentalFleetNetwork.set(sd.nameOfShortTermRentalFleetNetwork);

    // Sezione 6 - Risorse Umane
    if (sd.employeesCount != null) this.employeesCount.set(sd.employeesCount);
    if (sd.tinsmithsCount != null) this.tinsmithsCount.set(sd.tinsmithsCount);
    if (sd.preparersCount != null) this.preparersCount.set(sd.preparersCount);
    if (sd.paintersCount != null) this.paintersCount.set(sd.paintersCount);
    if (sd.assemblyWorkersCount != null) this.assemblyWorkersCount.set(sd.assemblyWorkersCount);
    if (sd.clerksCount != null) this.clerksCount.set(sd.clerksCount);
    if (sd.hasTrainingCourses !== undefined) this.hasTrainingCourses.set(sd.hasTrainingCourses);
    if (sd.hasSoftwarePreventivi !== undefined) this.hasSoftwarePreventivi.set(sd.hasSoftwarePreventivi);
    if (sd.totalComputers != null) this.totalComputers.set(sd.totalComputers);
    if (Array.isArray(sd.estimateSoftwareTypes) && sd.estimateSoftwareTypes.length > 0) {
      this.estimateSoftwareTypes.set(sd.estimateSoftwareTypes.map((s: any) => ({
        id: s.id ?? null,
        softwareType: s.softwareType ?? '',
        softwareTypeOther: s.softwareTypeOther ?? null,
      })));
    }

    // AXA_DEUTSCHE_BANK-only fields
    if (sd.hasWindshieldReplacement !== undefined) this.hasWindshieldReplacement.set(sd.hasWindshieldReplacement);
    if (sd.hasMotorcycleRepair !== undefined) this.hasMotorcycleRepair.set(sd.hasMotorcycleRepair);
    if (sd.hasRelativesInAxa) this.hasRelativesInAxa.set(sd.hasRelativesInAxa);
    if (sd.hasEconomicRelationships) this.hasEconomicRelationships.set(sd.hasEconomicRelationships);

    // Capoluogo di provincia — ora gestito tramite operationalAddress.provinceAdministrativeCenter (caricato sotto)

    // Operational location address (only for sedi operative)
    if (sd.operationalAddress) {
      const oa = sd.operationalAddress;
      this.opAddressProvince.set(oa.province || '');
      this.opAddressMunicipality.set(oa.municipality || '');
      this.opAddressRegion.set(oa.region || '');
      this.opAddressStreetType.set(oa.streetType || '');
      this.opAddressStreetName.set(oa.streetName || '');
      this.opAddressStreetNumber.set(oa.streetNumber != null ? String(oa.streetNumber) : '');
      this.opAddressZipCode.set(oa.zipCode || '');
      // provinceAdministrativeCenter is now on the address object
      if (oa.provinceAdministrativeCenter !== undefined && oa.provinceAdministrativeCenter !== null) {
        this.isProvinceCapital.set(oa.provinceAdministrativeCenter);
      }
      // Pre-load municipalities for the saved province, then restore municipality
      if (oa.province) {
        this.preloadOpAddressMunicipalities(oa.province, oa.municipality || '');
      }
    }

    // Wizard status
    if (sd.specificDataSectionStatus === 'FILLED') {
      console.log('[OK] Specific data section is FILLED - marking step as completed');
      this.professionalsWizardService.markStepCompleted(1, true);
    } else {
      console.log('[WARN] Specific data section status:', sd.specificDataSectionStatus, '- marking step as NOT completed');
      this.professionalsWizardService.markStepCompleted(1, false);
    }
    this.professionalsWizardService.saveState();

    // Collaborators (at root level, not in specificData)
    const collaborators = data.collaborators || [];
    if (collaborators.length > 0) {
      const collabs = collaborators.map((c: any, index: number) => ({
        id: index,
        surname: c.lastName || '',
        name: c.firstName || '',
        fiscalCode: c.fiscalCode || '',
        claimsSystemAccess: c.needsClaimsPortalAccess || false,
      }));
      this.collaborators.set(collabs);
      this.collaboratorIdCounter = collabs.length;
    }
  }

  markFieldAsTouched(fieldName: string): void {
    this.touchedFields.add(fieldName);
  }

  createEmptyCollaborator(): Collaborator {
    return {
      id: this.collaboratorIdCounter++,
      surname: '',
      name: '',
      fiscalCode: '',
      claimsSystemAccess: false,
    };
  }

  addCollaborator(): void {
    this.collaborators.update((curr) => [...curr, this.createEmptyCollaborator()]);
    const count = this.collaborators().length;
    this.liveRegionMessage.set(`Collaboratore ${count} aggiunto`);
    setTimeout(() => this.liveRegionMessage.set(''), 1000);
    setTimeout(() => {
      const inputs = this.firstInputs.toArray();
      const newInput = inputs[inputs.length - 1];
      if (newInput) newInput.nativeElement.focus();
    }, 100);
  }

  removeCollaborator(id: number): void {
    const index = this.collaborators().findIndex((c) => c.id === id);
    this.collaborators.update((curr) => curr.filter((c) => c.id !== id));
    this.liveRegionMessage.set(`Collaboratore ${index + 1} rimosso`);
    setTimeout(() => this.liveRegionMessage.set(''), 1000);
    setTimeout(() => {
      const inputs = this.firstInputs.toArray();
      const targetIndex = Math.max(0, index - 1);
      const targetInput = inputs[targetIndex];
      if (targetInput) targetInput.nativeElement.focus();
    }, 100);
  }

  isValidFiscalCode(fiscalCode: string): boolean {
    if (!fiscalCode) return false;
    return /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/i.test(fiscalCode);
  }

  hasCollaboratorStarted(collab: any): boolean {
    return !!(
      this.hasMeaningfulText(collab.surname) ||
      this.hasMeaningfulText(collab.name) ||
      this.hasMeaningfulText(collab.fiscalCode)
    );
  }

  getBodyShopTypeLabel(): string {
    if (this.isNetworkBodyShop()) {
      return 'Carrozzeria Network';
    }
    const typeMap: Record<string, string> = {
      STANDARD: 'STANDARD - Carrozzeria Standard',
      ARS: 'ARS - AXA Repair Services',
      RCI: 'RCI - Rete Carrozzerie Italiane',
      NOBIS: 'NOBIS - Carrozzeria NOBIS',
      AXA_DEUTSCHE_BANK: 'AXA Deutsche Bank',
    };
    return typeMap[this.bodyShopType()] || this.bodyShopType() || 'Non specificato';
  }

  // Repeatable items
  addBridge(): void {
    this.bridges.update((c) => [...c, { id: this.bridgeIdCounter++, bridgeType: '', bridgeTypeOther: '' }]);
    this.liveRegionMessage.set('Nuovo ponte aggiunto');
    setTimeout(() => this.liveRegionMessage.set(''), 3000);
  }

  removeBridge(bridgeId: number): void {
    this.bridges.update((c) => c.filter((b) => b.id !== bridgeId));
    this.liveRegionMessage.set('Ponte rimosso');
    setTimeout(() => this.liveRegionMessage.set(''), 3000);
  }

  addSpecialVehicle(): void {
    this.specialVehicles.update((c) => [...c, { id: this.specialVehicleIdCounter++, vehicleType: '', vehicleTypeOther: '' }]);
    this.liveRegionMessage.set('Tipologia veicolo aggiunta');
    setTimeout(() => this.liveRegionMessage.set(''), 3000);
  }

  removeSpecialVehicle(vehicleId: number): void {
    this.specialVehicles.update((c) => c.filter((v) => v.id !== vehicleId));
    this.liveRegionMessage.set('Tipologia veicolo rimossa');
    setTimeout(() => this.liveRegionMessage.set(''), 3000);
  }

  addFleet(): void {
    this.fleets.update((c) => [...c, { id: this.fleetIdCounter++, fleetName: '' }]);
    this.liveRegionMessage.set('Flotta aggiunta');
    setTimeout(() => this.liveRegionMessage.set(''), 3000);
  }

  removeFleet(fleetId: number): void {
    this.fleets.update((c) => c.filter((f) => f.id !== fleetId));
    this.liveRegionMessage.set('Flotta rimossa');
    setTimeout(() => this.liveRegionMessage.set(''), 3000);
  }

  addAuthorization(): void {
    this.authorizations.update((c) => [...c, { id: this.authorizationIdCounter++, carManufacturer: '', carManufacturerOther: '' }]);
    this.liveRegionMessage.set('Casa automobilistica aggiunta');
    setTimeout(() => this.liveRegionMessage.set(''), 3000);
  }

  removeAuthorization(authId: number): void {
    this.authorizations.update((c) => c.filter((a) => a.id !== authId));
    this.liveRegionMessage.set('Casa automobilistica rimossa');
    setTimeout(() => this.liveRegionMessage.set(''), 3000);
  }

  addIsoCertification(): void {
    this.isoCertifications.update((c) => [...c, { id: this.isoCertIdCounter++, isoCertification: '', otherIsoCertification: null }]);
    this.liveRegionMessage.set('Certificazione ISO aggiunta');
    setTimeout(() => this.liveRegionMessage.set(''), 3000);
  }

  removeIsoCertification(certId: number): void {
    this.isoCertifications.update((c) => c.filter((cert) => cert.id !== certId));
    this.liveRegionMessage.set('Certificazione ISO rimossa');
    setTimeout(() => this.liveRegionMessage.set(''), 3000);
  }

  getAvailableIsoOptions(currentId: number): { label: string; value: string }[] {
    const usedTypes = this.isoCertifications()
      .filter(c => c.id !== currentId && c.isoCertification !== 'OTHER' && c.isoCertification !== '')
      .map(c => c.isoCertification);
    return this.isoCertificationOptions().filter(opt => opt.value === 'OTHER' || !usedTypes.includes(opt.value));
  }

  getAvailableCarManufacturerOptions(currentId: number): { label: string; value: string }[] {
    const usedManufacturers = this.authorizations()
      .filter(a => a.id !== currentId && a.carManufacturer !== 'OTHER' && a.carManufacturer !== '')
      .map(a => a.carManufacturer);
    return this.carManufacturerOptions().filter(opt => opt.value === 'OTHER' || !usedManufacturers.includes(opt.value));
  }

  getAvailableSpecialVehicleOptions(currentId: number): { label: string; value: string }[] {
    const usedTypes = this.specialVehicles()
      .filter(v => v.id !== currentId && v.vehicleType !== 'OTHER' && v.vehicleType !== '')
      .map(v => v.vehicleType);
    return this.specialVehicleTypeOptions().filter(opt => opt.value === 'OTHER' || !usedTypes.includes(opt.value));
  }

  getAvailableEstimateSoftwareOptions(currentIndex: number): { label: string; value: string }[] {
    const usedTypes = this.estimateSoftwareTypes()
      .filter((_, i) => i !== currentIndex && _.softwareType !== 'OTHER' && _.softwareType !== '')
      .map(s => s.softwareType);
    return this.estimateSoftwareTypeOptions().filter(opt => opt.value === 'OTHER' || !usedTypes.includes(opt.value));
  }

  addEstimateSoftware(): void {
    this.estimateSoftwareTypes.update((c) => [...c, { id: null, softwareType: '', softwareTypeOther: null }]);
    this.liveRegionMessage.set('Preventivatore aggiunto');
    setTimeout(() => this.liveRegionMessage.set(''), 3000);
  }

  removeEstimateSoftware(index: number): void {
    this.estimateSoftwareTypes.update((c) => c.filter((_, i) => i !== index));
    this.liveRegionMessage.set('Preventivatore rimosso');
    setTimeout(() => this.liveRegionMessage.set(''), 3000);
  }

  // Form Submission
  validateAllFields(): boolean {
    this.formSubmitted.set(true);
    [
      'morningOpeningHours', 'afternoonOpeningHours', 'saturdayOpeningHours',
      'coveredArea', 'uncoveredArea', 'storageArea', 'customerReceptionArea',
      'paintBoothsCount', 'paintBoothsInstallationYear',
      'paintBrand', 'dryingType', 'liftsCount', 'dustExtractionFloors', 'dustExtractionArms',
      'rentalCarsCount', 'ownedCarsCount', 'ownedTowTrucksCount',
      'employeesCount', 'tinsmithsCount', 'preparersCount', 'paintersCount', 'assemblyWorkersCount', 'clerksCount',
    ].forEach((f) => this.touchedFields.add(f));

    const isValid = this.isFormValid();
    if (!isValid) {
      const allErrors = [
        ...this.opAddressErrors(),
        ...this.section1Errors(),
        ...this.section2Errors(),
        ...this.section3Errors(),
        ...this.section4Errors(),
        ...this.section5Errors(),
        ...this.section6Errors(),
      ];
      this.validationFailed.emit(validationSectionsFromMessages([
        { title: 'Sede operativa', errors: this.opAddressErrors() },
        { title: 'Informazioni struttura', errors: this.section1Errors() },
        { title: 'Servizi', errors: this.section2Errors() },
        { title: 'Riparazioni', errors: this.section3Errors() },
        { title: 'Flotte e certificazioni', errors: this.section4Errors() },
        { title: 'Convenzioni', errors: this.section5Errors() },
        { title: 'Risorse umane', errors: this.section6Errors() },
      ]));
      this.liveRegionMessage.set(`Form non valido: ${allErrors.length} ${allErrors.length === 1 ? 'errore' : 'errori'} trovati`);
      setTimeout(() => this.liveRegionMessage.set(''), 3000);
    }
    return isValid;
  }

  buildPayload(status: 'DRAFT' | 'FILLED' = 'DRAFT'): any {
    const isAxaDeutscheBank = this.isAxaDeutscheBank();
    const payload: any = {
      specificData: {
        specificDataSectionStatus: status,
        bodyShopType: this.bodyShopType() || 'STANDARD',

        // Sezione 1 - Informazioni Struttura
        isPresentOnline: this.isPresentOnline(),
        typePresenceOnline: this.typePresenceOnline() || null,
        webPresenceUrl: this.webPresenceUrl() || null,
        otherOnlinePresence: this.otherOnlinePresence() || null,
        manageGoogleMyBusiness: this.manageGoogleMyBusiness(),
        morningOpeningHours: this.morningOpeningHours() || null,
        afternoonOpeningHours: this.afternoonOpeningHours() || null,
        saturdayOpeningHours: this.saturdayOpeningHours() || null,
        coveredArea: this.coveredArea() ?? null,
        uncoveredArea: this.uncoveredArea() ?? null,
        storageArea: this.storageArea() ?? null,
        customerReceptionArea: this.customerReceptionArea() ?? null,
        hasAccessForDisabledPeople: this.hasAccessForDisabledPeople(),
        hasCustomerParking: this.hasCustomerParking(),
        operatingTerritories: this.operatingTerritories(),
        // Attrezzature
        paintBoothsCount: this.paintBoothsCount() ?? null,
        paintBoothsInstallationYear: this.paintBoothsInstallationYear() ?? null,
        paintBrand: this.paintBrand() || null,
        dryingType: this.dryingType() || null,
        dryingTypeOther: this.dryingType() === 'OTHER' ? (this.dryingTypeOther() || null) : null,
        isPresentTinometer: this.isPresentTinometer(),
        aluminumWork: this.aluminumWork(),
        specificEquipment: this.specificEquipment() || null,
        liftsCount: this.liftsCount() ?? null,
        bridges: this.bridges().map((b) => ({
          bridgeType: b.bridgeType || null,
          bridgeTypeOther: b.bridgeTypeOther || null,
        })),
        hasJig: this.hasJig(),
        jigType: this.hasJig() === true ? (this.jigType() || null) : null,
        jigTypeOther: this.hasJig() === true && this.jigType() === 'OTHER' ? (this.jigTypeOther() || null) : null,
        hasDigitalMeasurement: this.hasDigitalMeasurement(),
        digitalMeasurementType: this.hasDigitalMeasurement() === true ? (this.digitalMeasurementType() || null) : null,
        otherDigitalMeasurement: this.hasDigitalMeasurement() === true && this.digitalMeasurementType() === 'OTHER' ? (this.otherDigitalMeasurement() || null) : null,
        dustExtractionFloors: this.dustExtractionFloors() ?? null,
        dustExtractionArms: this.dustExtractionArms() ?? null,

        // Sezione 2 - Servizi
        hasWashingService: isAxaDeutscheBank ? null : this.hasWashingService(),
        washingServiceType: !isAxaDeutscheBank && this.hasWashingService() === true ? (this.washingServiceType() || null) : null,
        hasMechanicsService: this.hasMechanicsService(),
        mechanicsServiceType: this.hasMechanicsService() === true ? (this.mechanicsServiceType() || null) : null,
        hasElectronicDiagnostics: this.hasElectronicDiagnostics(),
        electronicDiagnosticsType: this.hasElectronicDiagnostics() === true ? (this.electronicDiagnosticsType() || null) : null,
        hasElectricalService: this.hasElectricalService(),
        electricalServiceType: this.hasElectricalService() === true ? (this.electricalServiceType() || null) : null,
        hasTireService: this.hasTireService(),
        tireServiceType: this.hasTireService() === true ? (this.tireServiceType() || null) : null,
        hasAirConditioningRecharge: this.hasAirConditioningRecharge(),
        airConditioningRechargeType: this.hasAirConditioningRecharge() === true ? (this.airConditioningRechargeType() || null) : null,
        hasMinisterialInspection: this.hasMinisterialInspection(),
        ministerialInspectionType: this.hasMinisterialInspection() === true ? (this.ministerialInspectionType() || null) : null,
        hasAdasSystemsCalibration: this.hasAdasSystemsCalibration(),
        adasSystemsCalibrationLocation: this.hasAdasSystemsCalibration() === true ? (this.adasSystemsCalibrationLocation() || null) : null,
        sellsMotorVehicles: this.sellsMotorVehicles(),

        // Sezione 3 - Riparazioni
        hasSmartServices: this.hasSmartServices(),
        hasHailDamageRepair: this.hasHailDamageRepair(),
        hasWindshieldRepair: this.hasWindshieldRepair(),
        hasFabricRepair: this.hasFabricRepair(),
        hasPlasticLeatherRepair: this.hasPlasticLeatherRepair(),
        hasRimRepair: this.hasRimRepair(),
        hasSpecificPointRepair: this.hasSpecificPointRepair(),
        makeRepairsUsingColdRepairTechniques: this.makeRepairsUsingColdRepairTechniques(),
        hasSpotRepairs: this.hasSpotRepairs(),
        hasSpecialVehicleRepairs: this.hasSpecialVehicleRepairs(),
        specialVehicles: this.specialVehicles().map((sv) => ({
          vehicleType: sv.vehicleType || null,
          vehicleTypeOther: sv.vehicleTypeOther || null,
        })),
        hasSubstituteCar: this.hasSubstituteCar(),
        rentalCarsCount: this.rentalCarsCount() ?? null,
        ownedCarsCount: this.ownedCarsCount() ?? null,
        hasRoadsideAssistance20km: this.hasRoadsideAssistance20km(),
        roadsideAssistanceType: this.roadsideAssistanceType() || null,
        ownedTowTrucksCount: this.ownedTowTrucksCount() ?? null,
        hasPickupDelivery20km: this.hasPickupDelivery20km(),
        hasInteriorWashing: this.hasInteriorWashing(),
        hasFinishingTouches: this.hasFinishingTouches(),
        hasEngineLevelCheck: this.hasEngineLevelCheck(),
        hasTirePressureCheck: this.hasTirePressureCheck(),
        hasAirConditioningCleaning: this.hasAirConditioningCleaning(),
        hasCabinSanitization: this.hasCabinSanitization(),
        hasMechanicalCheckup: this.hasMechanicalCheckup(),

        // Sezione 4 - Flotte e Certificazioni
        hasRentalVehicleRepairs: this.hasRentalVehicleRepairs(),
        fleets: this.fleets().map((f) => ({ fleetName: f.fleetName || null })),
        isTeslaAuthorized: this.isTeslaAuthorized(),
        authorizations: this.authorizations().map((a) => ({
          carManufacturer: a.carManufacturer || null,
          carManufacturerOther: a.carManufacturerOther || null,
        })),
        hasSafetyCertifications: this.hasSafetyCertifications(),
        hasSwornCertifications: this.hasSwornCertifications(),
        hasGreenCertifications: this.hasGreenCertifications(),
        greenCertifications: this.hasGreenCertifications() === true ? (this.greenCertifications() || null) : null,
        hasIsoCertifications: this.hasIsoCertifications(),
        isoCertifications: this.hasIsoCertifications() === true ? this.isoCertifications().map(c => ({
          isoCertification: c.isoCertification || null,
          otherIsoCertification: c.isoCertification === 'OTHER' ? (c.otherIsoCertification || null) : null,
        })) : null,
        hasHybridCourtesyCars: this.hasHybridCourtesyCars(),
        hasChargingColumns: this.hasChargingColumns(),
        hasEcologicalPaintCertifications: this.hasEcologicalPaintCertifications(),
        hasSolarPanels: this.hasSolarPanels(),
        hasEndothermicBoothsWithLowCo2Emission: this.hasEndothermicBoothsWithLowCo2Emission(),
        usesRecycledOeReplacementParts: this.usesRecycledOeReplacementParts(),
        usesDigitalTools: this.usesDigitalTools(),
        otherCertifications: this.otherCertifications() || null,

        // Sezione 5 - Convenzioni
        hasAgreementWithAxaAssistance: this.hasAgreementWithAxaAssistance(),
        hasAgreementWithOtherInsuranceCompanies: this.hasAgreementWithOtherInsuranceCompanies(),
        hasAgreementWithHailProviders: this.hasAgreementWithHailProviders(),
        hailProvidersInAgreement: this.hailProvidersInAgreement() || null,
        hasAgreementWithCrystalProviders: this.hasAgreementWithCrystalProviders(),
        crystalProvidersInAgreement: this.crystalProvidersInAgreement() || null,
        isPartOfNetwork: this.isPartOfNetwork(),
        nameOfNetwork: this.nameOfNetwork() || null,
        isAffiliatedWithLongTermRentalFleetNetwork: this.isAffiliatedWithLongTermRentalFleetNetwork(),
        nameOfLongTermRentalFleetNetwork: this.nameOfLongTermRentalFleetNetwork() || null,
        isAffiliatedWithShortTermRentalFleetNetwork: this.isAffiliatedWithShortTermRentalFleetNetwork(),
        nameOfShortTermRentalFleetNetwork: this.nameOfShortTermRentalFleetNetwork() || null,

        // Sezione 6 - Risorse Umane
        employeesCount: this.employeesCount() ?? null,
        tinsmithsCount: this.tinsmithsCount() ?? null,
        preparersCount: this.preparersCount() ?? null,
        paintersCount: this.paintersCount() ?? null,
        assemblyWorkersCount: this.assemblyWorkersCount() ?? null,
        clerksCount: this.clerksCount() ?? null,
        hasTrainingCourses: this.hasTrainingCourses(),
        hasSoftwarePreventivi: this.hasSoftwarePreventivi(),
        totalComputers: this.totalComputers() ?? null,
        estimateSoftwareTypes: this.estimateSoftwareTypes().map(s => ({
          softwareType: s.softwareType || null,
          softwareTypeOther: s.softwareTypeOther || null,
        })),

        // AXA_DEUTSCHE_BANK-only fields
        hasWindshieldReplacement: this.hasWindshieldReplacement(),
        hasMotorcycleRepair: this.hasMotorcycleRepair(),
        hasRelativesInAxa: isAxaDeutscheBank ? null : (this.hasRelativesInAxa() || null),
        hasEconomicRelationships: isAxaDeutscheBank ? null : (this.hasEconomicRelationships() || null),
        // Operational location address (only set when isOperationalLocation)
        ...(this.isOperationalLocation() && {
          operationalAddress: {
            province: this.opAddressProvince() || null,
            municipality: this.opAddressMunicipality() || null,
            region: this.opAddressRegion() || null,
            streetType: this.opAddressStreetType() || null,
            streetName: this.opAddressStreetName() || null,
            streetNumber: this.opAddressStreetNumber(),
            zipCode: this.opAddressZipCode() || null,
            provinceAdministrativeCenter: this.isProvinceCapital() ?? false,
          },
        }),
      },
    };

    console.log('[INFO] Bodyshop form payload:', payload);
    return payload;
  }

  saveDraft(): void {
    console.log('[INFO] Saving bodyshop draft...');

    const professionalId = this.professionalIdInput() || this.mockAuthService.getDemoProfessionalId();

    if (!professionalId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'ID Fiduciario non trovato',
        life: 5000,
      });
      return;
    }

    this.isSaving.set(true);
    const payload = this.buildPayload('DRAFT');

    this.professionalsService.updateBodyshopData(professionalId, payload).subscribe({
      next: (response: any) => {
        this.isSaving.set(false);
        console.log('[OK] Draft saved successfully:', response);

        this.messageService.add({
          severity: 'success',
          summary: 'Bozza Salvata',
          detail: 'I dati sono stati salvati in bozza',
          life: 3000,
        });

        this.onSaveDraft.emit(convertEmptyStringsToNull(payload));

        this.liveRegionMessage.set('Bozza salvata con successo');
        setTimeout(() => this.liveRegionMessage.set(''), 3000);
      },
      error: (err: any) => {
        this.isSaving.set(false);
        console.error('[ERR] Error saving draft:', err);

        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile salvare la bozza',
          life: 5000,
        });
      },
    });
  }

  saveAndProceed(): void {
    if (!this.validateAllFields()) {
      return;
    }

    console.log('[INFO] Saving bodyshop and proceeding...');

    const professionalId = this.professionalIdInput() || this.mockAuthService.getDemoProfessionalId();

    if (!professionalId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'ID Fiduciario non trovato',
        life: 5000,
      });
      return;
    }

    this.isSaving.set(true);
    const payload = this.buildPayload('FILLED');

    this.professionalsService.updateBodyshopData(professionalId, payload).subscribe({
      next: (response: any) => {
        this.isSaving.set(false);
        console.log('[OK] Data saved successfully:', response);

        this.messageService.add({
          severity: 'success',
          summary: 'Salvato',
          detail: 'Dati salvati con successo',
          life: 3000,
        });

        this.onSave.emit(convertEmptyStringsToNull(payload));

        this.liveRegionMessage.set('Dati salvati, passaggio allo step successivo');
        // La navigazione allo step successivo è gestita dal parent (professionals-specific-data)
        // tramite l'evento onSave, che tiene conto del flusso NK vs standard.
      },
      error: (err: any) => {
        this.isSaving.set(false);
        console.error('[ERR] Error saving data:', err);

        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile salvare i dati',
          life: 5000,
        });
      },
    });
  }
}



